import { fetchAsset } from "../core/AssetSource.js";
import { DEFAULT_FABRIC_CODE, RENAMED_PRESETS } from "../utils/constants.js";
import { runWhenIdle } from "../utils/idle.js";
import { appendImage, prefetchImage } from "../utils/imagePool.js";
import { assetUrl } from "../config.js";
import { fetchApiJson } from "../core/ApiData.js";
import { readHistory, readThumbnail, removeFromHistory } from "../core/DesignHistory.js";

// Shared by the modal card and its warm-up, which must land in the same image pool bucket.
const PRESET_THUMBNAIL_CLASS = 'layout-thumbnail-image';
const LAYOUT_OPTION_THUMBNAIL_CLASS = 'layout-option-img';
const presetThumbnailUrl = (id) => assetUrl(`thumbnails/presets/${id}.webp`);
// The combination that represents a collection on the modal's opening view — its shot, its
// description, its dimensions. `defaultCombination` in layouts.json picks it; without one it
// falls back to the `<id>-c` default-combination name, then to the first entry authored.
const defaultCombinationOf = (collection) => {
  const combinations = collection?.combinations;
  if (!combinations?.length) return undefined;
  return combinations.find(c => c.id === collection.defaultCombination)
    || combinations.find(c => c.id === `${collection.id}-c`)
    || combinations[0];
};

// Picker rows that stand for an action rather than a product, so they have no shot.
const RESUME_GLYPH = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
    <path d="M3 3v5h5"/>
    <path d="M12 7v5l4 2"/>
  </svg>`;
const SCRATCH_GLYPH = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M12 8v8"/>
    <path d="M8 12h8"/>
  </svg>`;

// The picker level listing saved designs; it rides the same history entries as a collection.
const HISTORY_VIEW_ID = '__history';
const TRASH_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>`;

// A saved design's capture, row or hero: a data URL, so it stays out of the shared image pool.
function captureImage(src, className, alt = '') {
  const img = document.createElement('img');
  img.src = src;
  img.alt = alt;
  img.className = `${className} layout-capture`;
  img.decoding = 'async';
  img.loading = 'lazy';
  return img;
}

// history.state key for a picker entry `{ collectionId?, depth }`; depth = picker entries above the editor's (0 at boot).
const PICKER_HISTORY_KEY = 'layoutPicker';

/** The layout-selection modal (boot gate + "change layout"), and the preset loading it drives. */
export class LayoutPickerModal {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;

    // Product descriptions fetched from API
    this.productDescriptions = null;
    // Boot gate: modal is up with no scene behind it, so it cannot be dismissed.
    this._layoutGateOpen = false;
  }

  /** Boot gate. A saved session is offered as the Resume card, never auto-restored. */
  async showLayoutSelectionModal() {
    if (new URLSearchParams(window.location.search).has('layout')) return;
    this._layoutGateOpen = true;
    // The boot entry is the picker; there is nothing behind it to go back to.
    await this.reopenLayoutSelectionModal({ history: 'replace' });
  }

  /**
   * Reopens the layout selection modal (for when user clicks a button to change layout)
   * @param {{history?: 'push'|'replace'|'none'}} [opts] pushed by default, so Back closes it again.
   */
  async reopenLayoutSelectionModal({ history = 'push' } = {}) {
    const modal = document.getElementById('layout-selection-modal');
    if (!modal) return;

    // Self-guards both fetches; needed because a ?layout= boot skips the first-open path.
    await this.loadCollectionsFromJSON();

    this._listenForHistory();
    if (!modal.classList.contains('modal-visible')) {
      this._editorAhead = false;
      this._writeHistory(history, { depth: history === 'push' ? 1 : 0 });
    }

    this.showCollectionsView();
    modal.classList.add('modal-visible');

    const backBtn = document.getElementById('back-to-collections-btn');
    if (backBtn) {
      backBtn.onclick = () => this.backToCollections();
    }

    const backToEditorBtn = document.getElementById('back-to-editor-btn');
    if (backToEditorBtn) {
      backToEditorBtn.onclick = () => this.backToEditor();
    }
  }

  // ----- history: the picker's levels are entries, so the browser's Back walks out of them -----

  _pickerEntry() {
    return window.history.state?.[PICKER_HISTORY_KEY] ?? null;
  }

  _writeHistory(mode, entry) {
    if (mode === 'none') return;
    const state = { ...window.history.state, [PICKER_HISTORY_KEY]: entry };
    try {
      if (mode === 'push') window.history.pushState(state, '', window.location.href);
      else window.history.replaceState(state, '', window.location.href);
    } catch (error) {
      console.warn('[layout picker] history update failed:', error);
    }
  }

  /** Turn the current entry into a plain editor entry: a layout was chosen from it. */
  _leavePickerHistory() {
    this._editorAhead = false;
    if (!this._pickerEntry()) return;
    const { [PICKER_HISTORY_KEY]: _, ...rest } = window.history.state;
    window.history.replaceState(Object.keys(rest).length ? rest : null, '', window.location.href);
  }

  _listenForHistory() {
    if (this._historyBound) return;
    this._historyBound = true;
    window.addEventListener('popstate', (event) => this._onPopState(event.state?.[PICKER_HISTORY_KEY] ?? null));
  }

  async _onPopState(entry) {
    const modal = document.getElementById('layout-selection-modal');
    const open = modal?.classList.contains('modal-visible');

    if (!entry) {
      // Editor entry: close the picker (never the boot gate, which has no scene).
      if (open && !this._layoutGateOpen) this.closeLayoutSelectionModal();
      this._editorAhead = false;
      return;
    }

    if (!open) {
      // Back from the editor onto the picker it was chosen in; Back to Editor goes forward again.
      await this.reopenLayoutSelectionModal({ history: 'none' });
      this._editorAhead = true;
    }
    if (entry.collectionId === HISTORY_VIEW_ID) {
      this.showHistoryView({ history: 'none' });
      return;
    }
    const collection = entry.collectionId && this.collectionsData?.find(c => c.id === entry.collectionId);
    if (collection) this.showCombinationsView(collection, { history: 'none' });
    else this.showCollectionsView();
  }

  /** Header Back and Android back: out of a collection, through history when that is what put us there. */
  backToCollections() {
    if (this._pickerEntry()?.collectionId) window.history.back();
    else this.showCollectionsView();
  }

  /** Back to Editor, undoing the picker's own history entries where it can. */
  backToEditor() {
    if (this._editorAhead) {
      window.history.forward();
      return;
    }
    const depth = this._pickerEntry()?.depth ?? 0;
    if (depth > 0) {
      window.history.go(-depth);
      return;
    }
    this._leavePickerHistory();
    this.closeLayoutSelectionModal();
  }

  /**
   * Best-effort warm-up of the layout modal's pre-baked webp thumbnails. Fetches
   * layouts.json (browser-cached, so the later modal fetch is free) and kicks off
   * high-priority image loads for the default combination of each collection — the
   * cards shown in the modal's initial (collections) view. Failures are ignored.
   */
  async preloadLayoutThumbnails() {
    try {
      const collections = await this.ensureCollectionsData();
      for (const collection of collections) {
        const def = defaultCombinationOf(collection);
        if (!def?.id || !def.compressed) continue;
        // Into the pool the cards draw from, so the modal shows this very element — warming
        // the network alone would leave it to decode a second copy. The pool is the GC anchor.
        // The hero and the picker row are separate buckets, and both are on screen at once.
        const url = presetThumbnailUrl(def.id);
        prefetchImage(url, PRESET_THUMBNAIL_CLASS);
        prefetchImage(url, LAYOUT_OPTION_THUMBNAIL_CLASS);
      }
      // Idle time while browsing the modal — warm the default fabric's 512 texture so the first preset opens without a fetch wait.
      this.scheduleDefaultTexturePrefetch();
    } catch {
      // preload is a best-effort optimization; the modal renders fine without it
    }
  }

  // Prefetch the default fabric (ET-14) at 512 during idle; MaterialManager may not be ready when this first fires (preload runs early), so re-check and reschedule a few times.
  scheduleDefaultTexturePrefetch(retries = 3) {
    runWhenIdle(() => {
      const mm = this.app.getMaterialManager?.();
      const id = mm?.getFabricIdByCode?.(DEFAULT_FABRIC_CODE);
      if (!id) {
        if (retries > 0) this.scheduleDefaultTexturePrefetch(retries - 1);
        return;
      }
      mm.getFabricData(id, '512').catch(() => {});
    }, { timeout: 4000, fallbackDelay: 1500 });
  }

  // Fetch + parse layouts.json once, caching on this.collectionsData. Shared by the
  // modal open path and the init-time thumbnail preload so layouts.json is fetched once.
  async ensureCollectionsData() {
    if (this.collectionsData && this.collectionsData.length > 0) {
      return this.collectionsData;
    }
    const response = await fetchAsset('data/layouts.json');
    if (!response.ok) throw new Error('Could not load layouts.json');
    const data = await response.json();
    this.collectionsData = data.collections || [];
    return this.collectionsData;
  }

  async loadCollectionsFromJSON() {
    try {
      // preloadLayoutThumbnails may have already fetched + parsed this during init.
      await this.ensureCollectionsData();

      await this.ensureProductDescriptions();
    } catch (error) {
      console.error('Error loading collections:', error);
      this.collectionsData = [];
    }
  }

  /**
   * Find a preset combination across all collections by its (globally unique) id,
   * returning it with collection context attached (the shape selectLayout expects).
   * @param {string} id e.g. "cascais-ra"
   * @returns {Object|null}
   */
  findCombinationById(id) {
    if (!id || !this.collectionsData) return null;
    for (const collection of this.collectionsData) {
      const combination = (collection.combinations || []).find(c => c.id === id);
      if (combination) {
        return { ...combination, collectionId: collection.id, collectionName: collection.name };
      }
    }
    // Renamed preset ids resolve to their replacement, so links shared under the old id keep working.
    const renamed = RENAMED_PRESETS[id];
    return renamed ? this.findCombinationById(renamed) : null;
  }

  /**
   * Load a preset layout by its id (the `?preset=<id>` URL entry point). Ensures the
   * catalog is loaded, resolves the id, and routes through the normal selectLayout path.
   * @param {string} id
   * @param {{fabricCode?: string|null, legColor?: string|null, legHeight?: string|null, boardColor?: string|null}} [overrides]
   *   Optional fabric / leg / wood overrides from the share link, applied once the preset loads.
   * @returns {Promise<boolean>} true if a matching preset was found and loaded
   */
  async loadLayoutById(id, overrides = null) {
    if (!this.collectionsData || this.collectionsData.length === 0) {
      await this.loadCollectionsFromJSON();
    }
    const combination = this.findCombinationById(id);
    if (!combination || !combination.compressed) {
      console.warn(`No preset found for id "${id}"`);
      return false;
    }
    await this.selectLayout(combination, overrides);
    return true;
  }

  /** One fetch shared by the picker and the selection label; a miss is retried on the next call. */
  ensureProductDescriptions() {
    this._descriptionsPromise ??= this.loadProductDescriptions().finally(() => {
      if (!this.productDescriptions) this._descriptionsPromise = null;
    });
    return this._descriptionsPromise;
  }

  async loadProductDescriptions() {
    const country = this.ui.localization?.getCountry() || 'GLOBAL';
    // The shipped snapshot is the GLOBAL (English) file; a country one is only there if
    // scripts/fetch-api-defaults.mjs was run for it.
    const data = await fetchApiJson('product-descriptions', {
      params: { country },
      snapshot: [`descriptions/${country}.json`, 'descriptions/GLOBAL.json'],
    });
    if (!data) {
      console.warn('No product descriptions available');
      return;
    }
    this.productDescriptions = data;
  }

  getProductDescription(productId) {
    if (!this.productDescriptions || !this.productDescriptions[productId]) return null;

    const entry = this.productDescriptions[productId];
    const lang = this.ui.localization?.getLanguage() || 'en';

    // Country-specific files use the local language key (e.g. "de" in DE file).
    // Try requested lang first, then English, then the first available language.
    const resolve = (field) => {
      if (!field) return null;
      return field[lang] || field['en'] || field[Object.keys(field)[0]] || null;
    };

    return {
      name: resolve(entry.name),
      description: resolve(entry.description),
      shortDescription: resolve(entry.shortDescription),
    };
  }

  // The same line as an element under a layout card. Null when there is nothing to show.
  createLayoutCardDimensions(productId) {
    const text = this.ui._productDimensionsText(productId);
    if (!text) return null;

    const el = document.createElement('div');
    el.className = 'layout-card-dimensions';
    el.textContent = text;
    return el;
  }

  showCollectionsView() {
    const collectionsView = document.getElementById('collections-view');
    const combinationsView = document.getElementById('combinations-view');
    const headerWelcome = document.getElementById('layout-header-welcome');
    const headerCollection = document.getElementById('layout-header-collection');

    if (!collectionsView || !combinationsView) return;

    // Show collections, hide combinations
    collectionsView.classList.remove('hidden');
    combinationsView.classList.add('hidden');

    // Show welcome header, hide collection header
    if (headerWelcome) headerWelcome.classList.remove('hidden');
    if (headerCollection) headerCollection.classList.add('hidden');

    // Populate collections grid
    const collectionsGrid = document.getElementById('collections-grid');
    if (!collectionsGrid) return;

    this.setupLayoutPicker(collectionsGrid);
    collectionsGrid.innerHTML = '';

    // Check if there's a saved session in localStorage
    const savedState = this.app.getStateManager()?.hasSavedSession();

    // Back-to-editor needs a scene behind the modal, which the boot gate does not have.
    const backToEditorBtn = document.getElementById('back-to-editor-btn');
    if (backToEditorBtn) {
      backToEditorBtn.classList.toggle('hidden', this._layoutGateOpen || !savedState);
    }

    // The autosaved session and every design saved with the Save icon live under History.
    const historyCount = readHistory().length;
    if (savedState || historyCount) {
      collectionsGrid.appendChild(this._actionRow(
        this.ui.t('designerModalHistory', 'History'),
        historyCount
          ? this.ui.t('designerModalHistoryDescription', 'Your saved designs and last session')
          : this.ui.t('designerModalResumeDescription', 'Continue where you left off'),
        () => this.showHistoryView(),
      ));
    }

    // Create collection cards for all enabled furniture collections
    const furnitureManager = this.app.getFurnitureManager();
    const furnitureCollections = furnitureManager?.getFurnitureCollections();
    const country = this.app.getLocalizationManager().getCountry();
    if (furnitureCollections) {
      for (const [collectionId, furnitureCollection] of Object.entries(furnitureCollections)) {
        // Skip hidden collections (e.g. internal/preconfigured items)
        if (furnitureCollection.hidden) continue;

        // Prefer layouts.json entry (has preset combinations), fall back to furniture data
        const layoutCollection = this.collectionsData.find(c => c.id === collectionId);
        const collection = layoutCollection || {
          id: collectionId,
          name: furnitureCollection.name,
          combinations: []
        };

        // Hide collection if it has combinations defined and all are unavailable for the country
        const combos = collection.combinations || [];
        if (combos.length > 0 && combos.every(c => !furnitureManager.isProductAvailable(c.id, country))) continue;

        const card = this.createCollectionOption(collection);
        collectionsGrid.appendChild(card);
      }
    }

    collectionsGrid.scrollTop = 0;
  }

  /**
   * Vertical list whose centred row is current and owns the one hero shot beside it.
   * Each option carries its own `_renderPreview`, so the picker needs no type knowledge.
   */
  setupLayoutPicker(list) {
    if (!list || list.dataset.pickerReady) return;
    list.dataset.pickerReady = '1';

    const view = list.closest('.layout-split');
    const thumb = view?.querySelector('.layout-preview-thumb');
    const text = view?.querySelector('.layout-preview-text');

    // Viewport coords on both sides: offsetTop is relative to the modal, not the list.
    const centreLine = () => {
      const pane = list.getBoundingClientRect();
      return pane.top + pane.height / 2;
    };
    const offsetFromCentre = (el, mid = centreLine()) => {
      const row = el.getBoundingClientRect();
      return (row.top + row.height / 2) - mid;
    };

    // Rows are in document order, so the distance falls until it passes the centre line.
    const centred = () => {
      const mid = centreLine();
      let best = null;
      let bestDistance = Infinity;
      for (const el of list.querySelectorAll('.layout-option')) {
        const distance = Math.abs(offsetFromCentre(el, mid));
        if (distance >= bestDistance) break;
        bestDistance = distance;
        best = el;
      }
      return best;
    };

    const centreOn = (el, behavior = 'smooth') =>
      list.scrollBy({ top: offsetFromCentre(el), behavior });
    list._centreOn = centreOn;

    // Half a pane top and bottom so the end rows can reach the centre line. Spacer boxes
    // (::before/::after), not scroller padding — padding-bottom isn't reliably scrollable.
    const padToCentre = () => {
      const first = list.querySelector('.layout-option');
      if (!first) return;
      const pad = Math.max(0, (list.clientHeight - first.offsetHeight) / 2);
      list.style.setProperty('--picker-pad', `${pad}px`);
    };

    let current = null;
    const sync = () => {
      const el = centred();
      if (!el || el === current) return;
      current?.classList.remove('is-current');
      current?.removeAttribute('aria-current');
      current = el;
      list._currentOption = el;
      el.classList.add('is-current');
      el.setAttribute('aria-current', 'true');
      el._renderPreview?.(thumb, text);
    };

    // Capture: the first click centres the row, the second commits; "centred" is measured now, not from the last scroll frame.
    list.addEventListener('click', (e) => {
      const option = e.target.closest('.layout-option');
      if (!option) return;
      if (Math.abs(offsetFromCentre(option)) <= option.offsetHeight / 2) {
        sync();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      centreOn(option);
    }, true);

    // One read per frame: a scroll fires far more often than the centred row changes.
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; sync(); });
    };

    const refresh = () => { padToCentre(); sync(); };

    list.addEventListener('scroll', onScroll, { passive: true });
    // Keep the centre line with the focus ring, or the hero describes another row.
    list.addEventListener('focusin', (e) => {
      const option = e.target.closest('.layout-option');
      if (option) centreOn(option);
    });
    new ResizeObserver(refresh).observe(list);
    // The combinations list is rebuilt per collection; re-read rather than keep a stale row.
    new MutationObserver(() => { current = null; refresh(); }).observe(list, { childList: true });

    // The hero drives the picker: wheel/drag scroll it, a click commits the centred row.
    const hero = view?.querySelector('.layout-preview');
    if (hero) {
      // deltaMode is lines (1) or pages (2) on some browsers, not always pixels (0).
      const wheelPixels = (e) => e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? list.clientHeight : 1);

      // Snap is suspended for the gesture's duration, as the browser does natively:
      // re-snapping every driven scroll would quantise it into row-sized jumps.
      let settleTimer = null;
      let snapHeld = false;
      const holdSnap = () => {
        clearTimeout(settleTimer);
        if (snapHeld) return;
        snapHeld = true;
        list.style.scrollSnapType = 'none';
      };
      const settle = (delay = 0) => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          const el = centred();
          if (el) centreOn(el);
          // After the settle animation, not before: re-arming mid-flight would jump.
          settleTimer = setTimeout(() => {
            snapHeld = false;
            list.style.scrollSnapType = '';
          }, 400);
        }, delay);
      };

      hero.addEventListener('wheel', (e) => {
        e.preventDefault();
        holdSnap();
        list.scrollBy({ top: wheelPixels(e) });
        // A wheel gesture has no end event; the pause between deltas is the only signal.
        settle(120);
      }, { passive: false });

      // Past the slop a press is a drag, not a click, and the click it ends with is swallowed.
      const DRAG_SLOP = 6;
      let drag = null;
      let dragMoved = false;

      hero.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        drag = { id: e.pointerId, y: e.clientY, from: list.scrollTop };
        dragMoved = false;
      });

      hero.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dy = e.clientY - drag.y;
        if (!dragMoved) {
          if (Math.abs(dy) < DRAG_SLOP) return;
          dragMoved = true;
          hero.setPointerCapture?.(e.pointerId);
          holdSnap();
        }
        // Content follows the pointer, so dragging up walks down the list.
        list.scrollTop = drag.from - dy;
      });

      const endDrag = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        if (dragMoved) {
          hero.releasePointerCapture?.(drag.id);
          settle();
        }
        drag = null;
      };
      hero.addEventListener('pointerup', endDrag);
      hero.addEventListener('pointercancel', endDrag);

      hero.addEventListener('click', () => {
        if (!dragMoved) list._currentOption?.click();
      });
    }

    refresh();
  }

  /**
   * One picker row: small shot + name. Description and dimensions live on the hero.
   * @param {{glyph?: string, combination?: Object}} preview also supplies the row's picture —
   *   a glyph where there is no product shot, else the webp the hero uses.
   */
  createLayoutOption(name, preview, onSelect) {
    const option = document.createElement('button');
    option.type = 'button';
    option.className = 'layout-option';

    const thumb = document.createElement('span');
    thumb.className = 'layout-option-thumb';
    if (preview.image) {
      thumb.appendChild(captureImage(preview.image, LAYOUT_OPTION_THUMBNAIL_CLASS));
    } else if (preview.glyph) {
      thumb.innerHTML = preview.glyph;
    } else if (preview.combination?.id) {
      // No live-render fallback at this size: a missing shot just leaves the slot empty.
      appendImage(thumb, [presetThumbnailUrl(preview.combination.id)], {
        alt: '',
        className: LAYOUT_OPTION_THUMBNAIL_CLASS,
        onExhausted: (img) => img.remove(),
      });
    }
    option.appendChild(thumb);

    const label = document.createElement('span');
    label.className = 'layout-option-name';
    label.textContent = name;
    option.appendChild(label);

    option._renderPreview = (heroThumb, text) => this._renderLayoutPreview(heroThumb, text, preview);
    option.addEventListener('click', onSelect);
    return option;
  }

  /**
   * The picture is full-bleed, so name/description/dimensions share one padded wrapper.
   */
  createLayoutCardText(name, description, dimensionsId) {
    const text = document.createElement('div');
    text.className = 'layout-card-text';
    for (const [cls, value] of [['layout-card-name', name],
                                ['layout-card-description', description]]) {
      const el = document.createElement('div');
      el.className = cls;
      el.textContent = value;
      text.appendChild(el);
    }
    const dimensions = dimensionsId ? this.createLayoutCardDimensions(dimensionsId) : null;
    if (dimensions) text.appendChild(dimensions);
    return text;
  }

  /** Paint the hero for one option: a glyph for the action rows, a saved design's capture, else the preset shot. */
  _renderLayoutPreview(thumb, text, { glyph, image, combination, name, description, dimensionsId, onDelete }) {
    if (text) {
      text.innerHTML = '';
      const card = this.createLayoutCardText(name, description, dimensionsId);
      if (onDelete) card.appendChild(this._deleteButton(onDelete));
      text.appendChild(card);
    }
    if (!thumb) return;

    // A scroll can outrun a live render, so the slow path checks it is still wanted.
    const token = (thumb._previewToken = (thumb._previewToken || 0) + 1);
    const isStale = () => thumb._previewToken !== token;

    thumb.classList.toggle('layout-preview-thumb--glyph', !image && !!glyph);
    if (image) {
      thumb.innerHTML = '';
      thumb.appendChild(captureImage(image, PRESET_THUMBNAIL_CLASS, name));
    } else if (glyph) {
      thumb.innerHTML = glyph;
    } else if (combination?.compressed) {
      thumb.innerHTML = '<span class="layout-thumbnail-loading">...</span>';
      this.generateLayoutThumbnailAsync(combination, thumb, isStale);
    } else {
      thumb.innerHTML = '';
    }
  }

  createCollectionOption(collection) {
    // The collection is represented by its default combination — that's the shot and the
    // description the catalog has for it.
    const defaultCombination = defaultCombinationOf(collection);
    const defaultId = defaultCombination?.id;
    const collectionDesc = defaultId ? this.getProductDescription(defaultId) : null;
    const option = this.createLayoutOption(
      collection.name,
      {
        combination: defaultCombination,
        name: collection.name,
        description: collectionDesc?.description || collection.description,
        dimensionsId: defaultId,
      },
      () => {
        // A collection with one preset is that product (q6, st-2): open it, not a list of one.
        const presets = this.availableCombinations(collection);
        if (presets.length === 1) this.selectLayout(presets[0]);
        else this.showCombinationsView(collection);
      },
    );
    option.dataset.collectionId = collection.id;
    return option;
  }

  /** @param {{history?: 'push'|'none'}} [opts] 'none' when history itself is restoring the view. */
  showCombinationsView(collection, { history = 'push' } = {}) {
    const combinationsGrid = this._enterListView(collection.id, collection.name, history);
    if (!combinationsGrid) return;

    // First, add "Start from Scratch" option
    const scratchCard = this.createCombinationOption({
      id: 'start-from-scratch',
      name: this.ui.t('designerModalStartFromScratch', 'Start from Scratch'),
      description: this.ui.t('designerModalEmptyRoomWith', 'Empty room with {collection} furniture', { collection: collection.name }),
      thumbnail: '',
      collectionId: collection.id,
      isStartFromScratch: true
    });
    combinationsGrid.appendChild(scratchCard);

    // Then add preset combination cards (filtered by country availability)
    const presetCards = this.availableCombinations(collection).map(combination => {
      const card = this.createCombinationOption(combination);
      combinationsGrid.appendChild(card);
      return card;
    });

    // Open on the first preset, not Start from Scratch; centring waits a frame for the spacer to lay out.
    combinationsGrid.scrollTop = 0;
    if (presetCards[0]) requestAnimationFrame(() => combinationsGrid._centreOn?.(presetCards[0], 'instant'));
  }

  /** From the editor's menu: the picker, opened straight onto History. */
  async openHistory() {
    await this.reopenLayoutSelectionModal();
    this.showHistoryView();
  }

  /** The last session, then saved designs newest first. @param {{history?: 'push'|'none', focusIndex?: number}} [opts] */
  showHistoryView({ history = 'push', focusIndex = 0 } = {}) {
    const grid = this._enterListView(HISTORY_VIEW_ID, this.ui.t('designerModalHistory', 'History'), history);
    if (!grid) return;

    const rows = [];
    if (this.app.getStateManager()?.hasSavedSession()) {
      rows.push(this._actionRow(
        this.ui.t('designerModalResumeSession', 'Resume Previous Session'),
        this.ui.t('designerModalResumeDescription', 'Continue where you left off'),
        () => this.selectLayout({ isResumeSession: true }),
      ));
    }
    for (const entry of readHistory()) rows.push(this.createHistoryOption(entry));
    if (!rows.length) {
      rows.push(this._actionRow(
        this.ui.t('designerHistoryEmpty', 'No saved designs yet'),
        this.ui.t('designerHistoryEmptyHint', 'Use the Save icon in the editor to keep a design here.'),
        () => {},
      ));
    }
    rows.forEach(row => grid.appendChild(row));

    grid.scrollTop = 0;
    const focus = rows[Math.min(focusIndex, rows.length - 1)];
    requestAnimationFrame(() => grid._centreOn?.(focus, 'instant'));
  }

  _actionRow(name, description, onSelect) {
    return this.createLayoutOption(name, { glyph: RESUME_GLYPH, name, description }, onSelect);
  }

  createHistoryOption({ id, name, savedAt, layout }) {
    const title = name || this.ui.t('designerHistoryUntitled', 'My design');
    const option = this.createLayoutOption(title, {
      image: readThumbnail(id),
      name: title,
      description: savedAt ? new Date(savedAt).toLocaleString(this.ui.localization?.getLanguage(), { dateStyle: 'medium', timeStyle: 'short' }) : '',
      onDelete: () => {
        if (!window.confirm(this.ui.t('designerHistoryDeleteConfirm', 'Delete this saved design?'))) return;
        const focusIndex = [...option.parentNode.children].indexOf(option);
        removeFromHistory(id);
        this.showHistoryView({ history: 'none', focusIndex });
      },
    }, () => this.selectLayout({ historyLayout: layout }));
    return option;
  }

  _deleteButton(onDelete) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'layout-card-action';
    button.innerHTML = `${TRASH_ICON}<span></span>`;
    button.lastElementChild.textContent = this.ui.t('designerButtonDelete', 'Delete');
    // The hero commits the centred row on click, so neither event may reach it.
    button.addEventListener('pointerdown', (e) => e.stopPropagation());
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      onDelete();
    });
    return button;
  }

  /** Swap the modal to its second level (a collection's presets, or History) and return the emptied list. */
  _enterListView(levelId, title, history) {
    if (history === 'push') {
      const parentDepth = this._pickerEntry()?.depth ?? 0;
      this._editorAhead = false;
      this._writeHistory('push', { collectionId: levelId, depth: parentDepth ? parentDepth + 1 : 0 });
    }

    const collectionsView = document.getElementById('collections-view');
    const combinationsView = document.getElementById('combinations-view');
    const headerWelcome = document.getElementById('layout-header-welcome');
    const headerCollection = document.getElementById('layout-header-collection');
    const collectionName = document.getElementById('layout-collection-name');

    if (!collectionsView || !combinationsView) return null;

    collectionsView.classList.add('hidden');
    combinationsView.classList.remove('hidden');
    headerWelcome?.classList.add('hidden');
    headerCollection?.classList.remove('hidden');
    if (collectionName) collectionName.textContent = title;

    const grid = document.getElementById('combinations-grid');
    if (!grid) return null;

    this.setupLayoutPicker(grid);
    grid.innerHTML = '';
    return grid;
  }

  /** The collection's presets this country sells, carrying their collection for selectLayout. */
  availableCombinations(collection) {
    const country = this.app.getLocalizationManager().getCountry();
    const furnitureManager = this.app.getFurnitureManager();
    return (collection.combinations || [])
      .filter(combination => furnitureManager.isProductAvailable(combination.id, country))
      .map(combination => ({ ...combination, collectionId: collection.id, collectionName: collection.name }));
  }

  createCombinationOption(combination) {
    const productDesc = this.getProductDescription(combination.id);
    const name = productDesc?.name || combination.name;

    const option = this.createLayoutOption(
      name,
      {
        glyph: combination.isStartFromScratch ? SCRATCH_GLYPH : null,
        combination: combination.isStartFromScratch ? null : combination,
        name,
        description: productDesc?.description || combination.description,
        dimensionsId: combination.isStartFromScratch ? null : combination.id,
      },
      () => this.selectLayout(combination),
    );
    option.dataset.combinationId = combination.id;
    return option;
  }

  /** @param {() => boolean} [isStale] the hero is shared, so a slow render re-checks it is still wanted. */
  generateLayoutThumbnailAsync(combination, thumbnailElement, isStale = () => false) {
    const id = combination.id;
    if (!id) {
      thumbnailElement.innerHTML = '';
      return;
    }

    // Prefer the pre-baked product shot (scripts/generate-thumbnails.mjs) so the
    // layout page doesn't pull every collection's models over the network. The
    // live renderer is only a fallback for sets that haven't been generated yet.
    thumbnailElement.innerHTML = '';
    appendImage(thumbnailElement, [presetThumbnailUrl(id)], {
      alt: combination.name || id,
      className: PRESET_THUMBNAIL_CLASS,
      // First thing on screen (layout modal), so fetch eagerly — the pre-baked webp is tiny and should win the network.
      eager: true,
      onExhausted: () => {
        if (isStale()) return;
        console.warn(`No pre-baked thumbnail for ${id}; rendering live`);
        this.renderLayoutThumbnailLive(combination, thumbnailElement, isStale);
      },
    });
  }

  async renderLayoutThumbnailLive(combination, thumbnailElement, isStale = () => false) {
    const thumbnailRenderer = this.app.getThumbnailRenderer();
    if (!thumbnailRenderer) {
      thumbnailElement.innerHTML = '';
      return;
    }

    try {
      // Pick fabric color from product-ordering for this set
      const materialManager = this.app.getMaterialManager();
      const fabricCode = materialManager?.productOrdering?.[combination.id] || null;

      const cacheKey = `layout_${combination.id}${fabricCode ? `_${fabricCode}` : ''}`;
      const dataUrl = await thumbnailRenderer.generateLayoutThumbnail(
        combination.compressed,
        cacheKey,
        { size: 256, fabricCode }
      );

      if (isStale()) return;
      if (dataUrl) {
        thumbnailElement.innerHTML = '';
        const img = document.createElement('img');
        img.src = dataUrl;
        img.alt = combination.name;
        img.className = 'layout-thumbnail-image';
        thumbnailElement.appendChild(img);
      } else {
        thumbnailElement.innerHTML = '';
      }
    } catch (error) {
      console.warn(`Failed to generate layout thumbnail for ${combination.id}:`, error);
      thumbnailElement.innerHTML = '';
    }
  }

  async selectLayout(layout, overrides = null) {
    // Show transition loader
    this.showTransitionLoader();

    // Start timer for minimum loader duration
    const startTime = Date.now();
    const minDuration = 500; // 500ms minimum

    // The entry it was chosen in now shows the editor; Back from it returns to the picker below.
    this._leavePickerHistory();
    this.closeLayoutSelectionModal();

    const stateManager = this.app.getStateManager();
    if (!stateManager) {
      this.hideTransitionLoader();
      return;
    }

    // Handle resume session
    // The last session or a saved design: somebody's own colours, so no preset preference applies.
    if (layout.isResumeSession || layout.historyLayout) {
      if (layout.isResumeSession) await stateManager.loadSceneState();
      else if (!await stateManager.loadDesign(layout.historyLayout)) {
        this.ui.showNotification(this.ui.t('designerHistoryLoadFailed', 'This saved design could not be opened.'), 'warning');
      }
      await this.ensureMinimumLoaderDuration(startTime, minDuration);
      this.hideTransitionLoader();
      return;
    }

    // Handle start from scratch with collection filter
    if (layout.isStartFromScratch && layout.collectionId) {
      // Clear any existing furniture from the scene
      const furnitureManager = this.app.getFurnitureManager();
      if (furnitureManager) {
        furnitureManager.clearAll();
        this.ui.updatePerformanceStats();
        await furnitureManager.spawnDefaultItems();
      }
      // Environment is a per-browser preference now — starting from scratch only clears furniture.
      await this.ui.setFurnitureCollectionFilter(layout.collectionId);
      this.ui.updateLegHeightDropdown();
      await this.ensureMinimumLoaderDuration(startTime, minDuration);
      this.hideTransitionLoader();
      return;
    }

    // If layout has compressed data, load it
    if (layout.compressed) {
      await stateManager.loadFromURL(layout.compressed);

      // Spawn the zip shelf for all layouts selected from the modal. Skipped in embed —
      // a read-only embed should show only the preset, not the editor's default shelf.
      const furnitureManager = this.app.getFurnitureManager();
      if (furnitureManager && !this.app.isEmbedMode) {
        await furnitureManager.spawnDefaultItems();
      }

      // Sync the catalog to the chosen layout's collection so the grid doesn't
      // keep showing whatever the previous layout selected. Skipped in embed, where
      // the grid UI isn't built (and updateFurnitureGrid would regenerate thumbnails).
      if (layout.collectionId && !this.app.isEmbedMode) {
        await this.ui.setFurnitureCollectionFilter(layout.collectionId, false);
        this.ui.updateLegHeightDropdown();
      }

      // A preset is a starting point, so the user's own colour wins over its catalog default.
      // Keep this immediately above markAsPreset: that captures the baseline signature, and
      // applying a preference must land inside the baseline or it reads as an edit and forks
      // the short `?preset=` link into a compressed `?layout=` one.
      await this.ui.applyLastSelectedColorToScene();

      // A named preset (has an id, not one of the synthetic modal actions) can be
      // shared via the short `?preset=<id>` URL until the user edits it.
      const presetId = layout.id && layout.id !== 'start-from-scratch' ? layout.id : null;
      if (presetId) {
        stateManager.markAsPreset(presetId);
      }
      // Survives edits, unlike markAsPreset — it selects the preset's path-tracing rig.
      stateManager.setLoadedPreset(presetId);

      // Apply optional fabric / leg overrides from the share link on top of the
      // freshly loaded preset (e.g. ?preset=...&fabric=HC-04&legColor=light&legHeight=10.5).
      if (overrides) {
        await this.applyPresetOverrides(overrides);
      }
    }

    // Ensure minimum loader duration before hiding
    await this.ensureMinimumLoaderDuration(startTime, minDuration);
    this.hideTransitionLoader();
  }

  /**
   * Apply URL-driven preset overrides (fabric / leg color / leg height / wood finish) on
   * top of a freshly loaded preset. Each override is independent and silently skipped when
   * absent or invalid. Entry point:
   * `?preset=<id>&fabric=HC-04&legColor=light&legHeight=10.5&boardColor=dark`.
   * @param {{fabricCode?: string|null, legColor?: string|null, legHeight?: string|null, boardColor?: string|null}} overrides
   */
  async applyPresetOverrides(overrides) {
    if (!overrides) return;
    const { fabricCode, legColor, legHeight, boardColor } = overrides;
    const furnitureManager = this.app.getFurnitureManager();
    if (!furnitureManager) return;

    // Fabric: resolve the catalog code (e.g. "HC-04") to a fabric id, then apply it only
    // to items the fabric is actually available for. Two failure modes are caught here so
    // an override can never produce an order link the backend rejects:
    //   - dev-only fabrics (`__` groups) aren't loaded in production → resolve to null.
    //   - country/product-restricted fabrics resolve but fail isFabricAllowedForProduct.
    if (fabricCode) {
      const materialManager = this.app.getMaterialManager();
      const fabricId = materialManager?.getFabricIdByCode(fabricCode);
      if (!fabricId) {
        this.ui.showNotification(this.ui.t('designerNotifyFabricUnavailable', 'Fabric "{fabric}" isn\'t available here — keeping the preset\'s fabric.', { fabric: fabricCode }), 'warning');
      } else {
        const { applied, skipped } = await this.ui._applyFabricToScene(fabricId);

        if (applied === 0) {
          this.ui.showNotification(this.ui.t('designerNotifyFabricUnavailableLayout', 'Fabric "{fabric}" isn\'t available for this layout — keeping the preset\'s fabric.', { fabric: fabricCode }), 'warning');
        } else {
          // Keep the picker and future placements in sync with the override.
          this.ui.selectedMaterial = fabricId;
          if (skipped > 0) {
            this.ui.showNotification(this.ui.t('designerNotifyFabricPartial', 'Fabric "{fabric}" isn\'t available for every item — applied where allowed.', { fabric: fabricCode }), 'info');
          }
        }
      }
    }

    // Leg color: "light" or "dark".
    if (legColor) {
      const legType = legColor.toLowerCase();
      if (legType === 'light' || legType === 'dark') {
        furnitureManager.updateAllLegTextures(legType);
      } else {
        this.ui.showNotification(this.ui.t('designerNotifyLegColorInvalid', 'Leg color "{value}" is invalid (expected "light" or "dark").', { value: legColor }), 'warning');
      }
    }

    // Wood finish ("light" | "dark") for wood-surface items (oiko-wst / oiko-sta). Applies
    // to every wood surface in scene and becomes the default for later placements.
    if (boardColor) {
      const woodType = boardColor.toLowerCase();
      if (woodType === 'light' || woodType === 'dark') {
        furnitureManager.setAllWoodTypes(woodType);
        // Keep the per-element picker in sync if something is selected (non-embed).
        this.ui.updateWoodFinishButton(furnitureManager.getSelectedFurnitureArray?.() || []);
      } else {
        this.ui.showNotification(this.ui.t('designerNotifyWoodFinishInvalid', 'Wood finish "{value}" is invalid (expected "light" or "dark").', { value: boardColor }), 'warning');
      }
    }

    // Leg height: must be one of the API-provided heights for the legged collections in
    // scene — a value present in dev's leg-height data (or just a typo) but not in this
    // build/country is rejected rather than baked into the order link. Match on numeric
    // value so the catalog's canonical string (what currentLegHeight must carry) is used.
    if (legHeight != null && legHeight !== '') {
      const availableHeights = this.ui.getAvailableLegHeightsForScene();
      const canonical = availableHeights.find(h => parseFloat(h) === parseFloat(legHeight));
      if (canonical) {
        furnitureManager.applyLegHeightToAll(canonical, { animate: false });
        this.ui.updateLegHeightDropdown();
      } else {
        this.ui.showNotification(this.ui.t('designerNotifyLegHeightUnavailable', 'Leg height "{height} cm" isn\'t available for this layout — keeping the preset\'s height.', { height: legHeight }), 'warning');
      }
    }

    // Persist the overridden look so a refresh / share captures it.
    this.app.notifyStateChanged?.();
  }

  async ensureMinimumLoaderDuration(startTime, minDuration) {
    const elapsed = Date.now() - startTime;
    if (elapsed < minDuration) {
      await new Promise(resolve => setTimeout(resolve, minDuration - elapsed));
    }
  }

  showTransitionLoader() {
    const loader = document.getElementById('layout-transition-loader');
    if (loader) {
      loader.classList.add('active');
    }
  }

  hideTransitionLoader() {
    const loader = document.getElementById('layout-transition-loader');
    if (loader) {
      loader.classList.remove('active');
    }
  }

  closeLayoutSelectionModal() {
    const modal = document.getElementById('layout-selection-modal');
    if (modal) {
      modal.classList.remove('modal-visible');
    }
    // Whatever closed it, a scene is on screen from here on — later opens are reopens.
    this._layoutGateOpen = false;
    this.ui.showDragHint();
  }
}
